import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';

const notifEnter = keyframes`
  0% { transform: translateX(120%) scale(0.8) rotate(3deg); opacity: 0; }
  40% { transform: translateX(-8%) scale(1.03) rotate(-1deg); opacity: 1; }
  55% { transform: translateX(3%) scale(0.98) rotate(0.5deg); }
  70% { transform: translateX(-1%) scale(1.01); }
  80% { transform: translateX(0) scale(1); opacity: 1; }
  100% { transform: translateX(0) scale(1); opacity: 1; }
`;
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 15px rgba(97,160,175,0.3), 0 8px 30px rgba(0,0,0,0.2); }
  50% { box-shadow: 0 0 30px rgba(97,160,175,0.6), 0 8px 40px rgba(0,0,0,0.3); }
`;
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
const iconBounce = keyframes`
  0%, 100% { transform: scale(1); }
  30% { transform: scale(1.4) rotate(-10deg); }
  50% { transform: scale(1.2) rotate(5deg); }
  70% { transform: scale(1.3) rotate(-3deg); }
`;
const progressShrink = keyframes`
  0% { width: 100%; }
  100% { width: 0%; }
`;

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [overlayNotifs, setOverlayNotifs] = useState<any[]>([]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close the mobile drawer whenever the route changes (backup in case a nav
  // item was clicked that doesn't go through Sidebar's own navigateAndClose).
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  const handleNewNotifs = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (detail?.notifications?.length) {
      setOverlayNotifs(detail.notifications.map((n: any) => ({ ...n, _key: `${n.id}-${Date.now()}` })));
      setTimeout(() => setOverlayNotifs([]), 5000);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('mspdw:new-notifications', handleNewNotifs);
    return () => window.removeEventListener('mspdw:new-notifications', handleNewNotifs);
  }, [handleNewNotifs]);

  return (
    <>
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        <Sidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Dedicated mobile header strip — only on mobile, sits above all page content. */}
          <Box sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            px: '12px', py: '10px',
            bgcolor: '#fff',
            borderBottom: '1px solid #e8e8e6',
            flexShrink: 0,
          }}>
            <Box
              component="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation"
              sx={{
                width: 40, height: 40, borderRadius: '8px',
                bgcolor: '#fff', color: '#003554',
                border: '1px solid #e0dbd4',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                p: 0,
                transition: 'background 0.15s ease, border-color 0.15s ease',
                '&:hover': { bgcolor: '#f5f2ee', borderColor: '#003554' },
                '&:active': { bgcolor: '#edeae4' },
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <line x1="9" y1="4" x2="9" y2="20" />
              </svg>
            </Box>
          </Box>
          <Outlet />
        </Box>
      </Box>

      {/* Notification overlay — rendered OUTSIDE the flex container, last in DOM */}
      {overlayNotifs.length > 0 && (
        <Box sx={{ position: 'fixed', top: 20, right: 24, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {overlayNotifs.map((n: any, i: number) => {
            const icon = n.type === 'email_received' ? '📧' : n.type === 'task_overdue' ? '⏰' : n.type === 'booking_received' ? '📅' : n.type === 'automation' ? '⚡' : '🔔';
            return (
              <Box key={n._key ?? i} onClick={() => { setOverlayNotifs([]); if (n.contactId) navigate(`/contacts/${n.contactId}`); }}
                sx={{
                  position: 'relative', overflow: 'hidden', cursor: 'pointer', minWidth: 340, maxWidth: 440,
                  bgcolor: 'rgba(0,53,84,0.92)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                  color: '#fff', borderRadius: '14px', p: '16px 20px',
                  border: '1px solid rgba(97,160,175,0.3)',
                  animation: `${notifEnter} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards, ${glowPulse} 2s ease-in-out infinite`,
                  animationDelay: `${i * 0.12}s, ${i * 0.12 + 0.7}s`,
                  '&:hover': { transform: 'scale(1.02)', border: '1px solid rgba(97,160,175,0.6)' },
                  transition: 'transform 0.2s ease, border 0.2s ease',
                  '&::before': {
                    content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '14px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%)',
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 2s ease-in-out 1`,
                    animationDelay: `${i * 0.12 + 0.5}s`,
                    pointerEvents: 'none',
                  },
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Box sx={{ fontSize: '28px', animation: `${iconBounce} 0.8s ease`, animationDelay: `${i * 0.12 + 0.3}s`, animationFillMode: 'both' }}>{icon}</Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>{n.title}</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#a8c8d0', lineHeight: 1.4, mt: '2px' }}>{n.message}</Typography>
                  </Box>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#61A0AF', boxShadow: '0 0 8px rgba(97,160,175,0.6)', flexShrink: 0 }} />
                </Box>
                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '0 0 14px 14px', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', bgcolor: '#61A0AF', borderRadius: '0 0 14px 14px',
                    animation: `${progressShrink} 5s linear forwards`,
                    animationDelay: `${i * 0.12}s`,
                  }} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}
